import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { refreshCrew } from '../crew/refreshCrew';
import { DialogLayer } from '../ui/Dialog';
import { PEOPLE_SETTINGS_KEY, type PeopleSettings } from './peopleGrid';

/**
 * „Dostępność → Ludzie” ⚙ (stage 1b K): the open options of the people centre in one place. Everyone with the grid
 * sees them; the owner changes them.
 */
export function PeopleSettingsDialog({ editable, onClose }: { editable: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const current = useQuery({ queryKey: PEOPLE_SETTINGS_KEY, queryFn: () => api<PeopleSettings>('/api/crew/people-settings') });
  const [draft, setDraft] = useState<PeopleSettings | null>(null);
  useEffect(() => {
    if (current.data && !draft) setDraft(current.data);
  }, [current.data, draft]);
  const save = useMutation({
    mutationFn: (s: PeopleSettings) => {
      const { version, ...rest } = s;
      return api<PeopleSettings>('/api/crew/people-settings', { method: 'PATCH', body: { version, ...rest } });
    },
    onSuccess: async (s) => {
      queryClient.setQueryData(PEOPLE_SETTINGS_KEY, s);
      await refreshCrew(queryClient);
      onClose();
    },
  });
  const set = <K extends keyof PeopleSettings>(key: K, value: PeopleSettings[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));
  const check = (key: 'withdrawalEmail' | 'cancelledCount' | 'namesNeedPersonsView' | 'questionCountsAnswered' | 'missingPastDays', label: string, hint: string) => (
    <label className="check pg-setting">
      <input type="checkbox" disabled={!editable} checked={draft?.[key] ?? false} onChange={(e) => set(key, e.target.checked)} />
      <span>
        {label}
        <span className="muted"> — {hint}</span>
      </span>
    </label>
  );
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="pg-settings-title" data-feature-id="crew.people_availability">
        <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="pg-settings-title">Ustawienia: Dostępność → Ludzie</h2>
        {!editable && <p className="muted">Ustawienia zmienia właściciel.</p>}
        {current.isLoading && <p className="muted">Wczytywanie…</p>}
        {draft && (
          <div className="pg-settings">
            <label className="field">
              <span>Kiedy kończy się „nieobsadzone”</span>
              <select disabled={!editable} value={draft.unstaffedEnds} onChange={(e) => set('unstaffedEnds', e.target.value as PeopleSettings['unstaffedEnds'])}>
                <option value="filled">gdy ktoś zajmie to miejsce</option>
                <option value="filled_or_manual">gdy ktoś zajmie to miejsce albo ręcznie („Oznacz jako obsadzone”)</option>
              </select>
            </label>
            {check('withdrawalEmail', 'Powiadomienie o wycofaniu także e-mailem', 'oprócz dzwonka')}
            {check('cancelledCount', 'Wydarzenia anulowane się liczą', 'widać je w tabeli i dają kolizje (także „Rezygnacja klienta”)')}
            {check('namesNeedPersonsView', 'Nazwiska tylko z prawem „Osoby: Podgląd”', 'bez niego w wierszu jest funkcja osoby, np. „Technik”')}
            {check('questionCountsAnswered', 'Odpowiedź „Mam pytanie” liczy się jako odpowiedź', 'inaczej kratka dalej pokazuje „zapytanie”')}
            {check('missingPastDays', 'Pokazuj braki w minionych dniach', 'czerwone liczby w wierszu braków także przed dzisiejszym dniem; wyłączone — tylko od dziś')}
          </div>
        )}
        {save.error instanceof ApiError && (
          <p className="alert" role="alert">
            {save.error.status === 409 ? 'Ktoś właśnie zmienił ustawienia. Zamknij i otwórz ponownie.' : save.error.message}
          </p>
        )}
        <div className="actions">
          {editable && (
            <button className="btn primary" type="button" disabled={!draft || save.isPending} onClick={() => draft && save.mutate(draft)}>
              {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            {editable ? 'Anuluj' : 'Zamknij'}
          </button>
        </div>
      </div>
    </div>
  );
}
